import React, { useEffect } from 'react';
import { AlertCircle, CheckCircle2, LoaderCircle, X } from 'lucide-react';


const ACTIVE_STATUSES = new Set(['queued', 'running']);

function statusPresentation(job, error) {
  if (error || job?.status === 'failed') {
    return {
      Icon: AlertCircle,
      iconClass: 'text-red-600',
      title: 'Report failed',
      detail: error || job?.error || 'The job did not complete.',
    };
  }
  if (job?.status === 'succeeded') {
    return {
      Icon: CheckCircle2,
      iconClass: 'text-emerald-600',
      title: 'Report ready',
      detail: job.progress?.message || 'The job completed successfully.',
    };
  }
  return {
    Icon: LoaderCircle,
    iconClass: 'animate-spin text-blue-600',
    title: job?.status === 'queued' ? 'Report queued' : 'Running report',
    detail: job?.progress?.message || 'Starting...',
  };
}

export default function AdminJobModal({ isOpen, title, job, error = '', notice = '', onClose, children }) {
  useEffect(() => {
    if (!isOpen) return undefined;
    const handleKeyDown = event => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const active = !error && (!job || ACTIVE_STATUSES.has(job.status));
  const progress = job?.progress || { current: 0, total: 1 };
  const progressPercent = Math.min(
    100,
    Math.round((Number(progress.current) / Math.max(Number(progress.total), 1)) * 100),
  );
  const presentation = statusPresentation(job, error);
  const Icon = presentation.Icon;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 p-4">
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="admin-job-modal-title"
        className="flex max-h-[calc(100vh-2rem)] w-full max-w-xl flex-col overflow-hidden rounded-lg border border-gray-200 bg-white shadow-2xl"
      >
        <div className="flex items-center justify-between border-b border-gray-200 px-5 py-4">
          <h2 id="admin-job-modal-title" className="text-base font-semibold text-gray-900">{title}</h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close report status"
            title="Close"
            className="inline-flex h-8 w-8 items-center justify-center rounded-md text-gray-500 hover:bg-gray-100 hover:text-gray-800"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="overflow-y-auto px-5 py-5">
          <div className="flex items-start gap-3">
            <Icon className={`mt-0.5 h-5 w-5 flex-none ${presentation.iconClass}`} />
            <div className="min-w-0 flex-1">
              <p className="font-medium text-gray-900">{presentation.title}</p>
              <p className={`mt-1 text-sm ${error || job?.status === 'failed' ? 'text-red-700' : 'text-gray-600'}`}>
                {presentation.detail}
              </p>
            </div>
          </div>

          {active && (
            <div className="mt-5">
              <div className="mb-1.5 flex items-center justify-between text-xs text-gray-500">
                <span>{job?.status === 'queued' ? 'Waiting for worker' : 'Progress'}</span>
                <span className="tabular-nums">{progressPercent}%</span>
              </div>
              <div className="h-2 overflow-hidden rounded-full bg-gray-200">
                <div
                  className="h-full bg-blue-600 transition-all duration-300"
                  style={{ width: `${progressPercent}%` }}
                />
              </div>
            </div>
          )}

          {notice && active && <p className="mt-4 text-xs text-amber-700">{notice}</p>}
          {job?.status === 'succeeded' && children && <div className="mt-5 border-t border-gray-200 pt-5">{children}</div>}
        </div>

        <div className="flex justify-end border-t border-gray-200 bg-gray-50 px-5 py-3">
          <button
            type="button"
            onClick={onClose}
            className="h-9 rounded-md border border-gray-300 bg-white px-4 text-sm font-medium text-gray-700 hover:bg-gray-100"
          >
            {active ? 'Run in background' : 'Close'}
          </button>
        </div>
      </div>
    </div>
  );
}
